import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useConversationHistory } from "src/api/conversationHistory";
import type { ConversationHistoryItem } from "src/api/conversationHistory";
import ConversationHistoryResults from "./ConversationHistoryResults";

interface ConversationHistoryResultsPanelProps {
  searchQuery: string;
  enabled: boolean;
  inProject: boolean;
  onOpen: (item: ConversationHistoryItem) => void;
  /**
   * Prefixes every data-testid below except `loadMoreErrorTestId` (kept
   * explicit — see its own doc).
   */
  testIdPrefix: string;
  /**
   * data-testid for the "couldn't load more" caption. Not derived from
   * `testIdPrefix`: the modal's existing id for this predates the page and
   * doesn't fit the prefix pattern (`conversation-history-page-error`,
   * confusingly — nothing to do with this being "the page"). Left as-is
   * rather than renamed as a side effect of this extraction.
   */
  loadMoreErrorTestId: string;
  /**
   * data-attr for the load-more button. The page's value intentionally
   * matches `AllChats`' own attr (it replaces `AllChats` for Atom Assist, and
   * this keeps the analytics event name continuous across that swap), so
   * it isn't derived from `testIdPrefix` either.
   */
  loadMoreDataAttr: string;
  /**
   * Skip the degraded-search-backend note here; the caller renders its own
   * instead (the modal pins it outside its scrollable results region, so it
   * stays visible regardless of list length). Defaults to false — render it
   * inline, which is what the page (no separate scroll region) wants.
   */
  hideDegradedNote?: boolean;
}

/**
 * The fetch and rendering for one In projects / General scope: loading,
 * error, empty, results, load-more, and (unless suppressed) the
 * degraded-search note.
 *
 * Mounted with `key={inProject}` (or an equivalent scope key) by the caller
 * so a scope switch is a real unmount/remount, not a query-key change inside
 * one observer. react-query v3's `keepPreviousData` re-reads the option on
 * every render rather than latching it at key-change time, so a flag that
 * only suppresses it for the render where the key changes doesn't hold — the
 * *next* render (the fetch-dispatch notify once the new request starts)
 * re-applies it, bridging the other scope's rows back in for the whole
 * in-flight window. A fresh mount has no previous observer result to bridge
 * from; the per-scope cache entry (`inProject` is part of the query key)
 * still makes switching back instant.
 */
export default function ConversationHistoryResultsPanel({
  searchQuery,
  enabled,
  inProject,
  onOpen,
  testIdPrefix,
  loadMoreErrorTestId,
  loadMoreDataAttr,
  hideDegradedNote = false,
}: ConversationHistoryResultsPanelProps) {
  const {
    items,
    total,
    searchBackend,
    isLoading,
    isInitialError,
    isPageError,
    canLoadMore,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useConversationHistory(searchQuery, enabled, inProject);

  const searching = searchQuery.trim().length > 0;
  const showEmptyState = !isLoading && !isInitialError && items.length === 0;
  // Pages already loaded stay rendered when a *next*-page fetch fails; only a
  // failed first load replaces the list with the error state.
  const showResults = !isLoading && items.length > 0;

  return (
    <>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {isInitialError && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 4,
          }}
          data-testid={`${testIdPrefix}-error`}
        >
          <Typography color="text.secondary" align="center">
            Something went wrong loading your history.
          </Typography>
          <Button
            size="small"
            onClick={() => void refetch()}
            data-testid={`${testIdPrefix}-retry`}
            data-attr="conversation-history:btn:retry"
          >
            Try again
          </Button>
        </Box>
      )}

      {showEmptyState && (
        <Typography
          color="text.secondary"
          align="center"
          sx={{ py: 4 }}
          data-testid={`${testIdPrefix}-empty`}
        >
          {searching
            ? `No conversations match "${searchQuery.trim()}"`
            : "No conversations yet"}
        </Typography>
      )}

      {showResults && (
        <>
          <ConversationHistoryResults items={items} onOpen={onOpen} />
          {isPageError && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ display: "block", py: 1 }}
              data-testid={loadMoreErrorTestId}
            >
              Couldn&apos;t load more conversations.
            </Typography>
          )}
          {(canLoadMore || isPageError) && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <Button
                size="small"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                data-testid={`${testIdPrefix}-load-more`}
                data-attr={loadMoreDataAttr}
              >
                {isFetchingNextPage
                  ? "Loading..."
                  : isPageError
                    ? "Try again"
                    : // `total` is only a bound off the search path; on it,
                      // Manticore caps the count, so don't quote it.
                      searching
                      ? "Load more"
                      : `Load more (${items.length} of ${total})`}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* The backend degrades to a title-only database filter when the
          full-text index is unavailable; say so instead of silently
          returning narrower results. */}
      {!hideDegradedNote && searchBackend === "database" && searching && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            color="text.disabled"
            data-testid={`${testIdPrefix}-degraded-note`}
          >
            Full-text search is unavailable; matching titles only.
          </Typography>
        </Box>
      )}
    </>
  );
}
